import { useMemo, useState } from "react";
import {
  Body1,
  Button,
  Caption1,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Add24Regular, ArrowClockwise24Regular, Folder20Regular, Search20Regular } from "@fluentui/react-icons";
import { useData } from "../data/DataProvider";
import { useServizi } from "../data/useServizi";
import { eAlbo, loadGare, oggettoGara, ordinaAlbi, statoIscrizione, testoRicerca, type Gara, type StatoIscrizione } from "../data/gare";
import { useAsync } from "../lib/useAsync";
import { formatDate } from "../lib/format";
import { navigate, replace } from "../lib/nav";
import { Shell } from "../components/Shell";
import { ChipGroup } from "../components/ChipGroup";
import { IscrizioneBadge } from "../components/Badges";
import { ErrorState } from "../components/ErrorState";
import { Codice } from "../components/Codice";

const useStyles = makeStyles({
  toolbar: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalM, flexWrap: "wrap" },
  search: { flexGrow: 1, minWidth: "260px" },
  count: { color: tokens.colorNeutralForeground3 },
  empty: { textAlign: "center", color: tokens.colorNeutralForeground2, padding: tokens.spacingVerticalXXL },
  tableWrap: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    overflowX: "auto",
  },
  row: { cursor: "pointer" },
  codice: { display: "block", fontWeight: tokens.fontWeightSemibold, fontVariantNumeric: "tabular-nums", paddingTop: tokens.spacingVerticalS, paddingBottom: tokens.spacingVerticalS },
  oggetto: { display: "flex", flexDirection: "column", gap: "2px", paddingTop: tokens.spacingVerticalS, paddingBottom: tokens.spacingVerticalS },
  titolo: { fontWeight: tokens.fontWeightSemibold },
  sub: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  cartella: { display: "inline-flex", alignItems: "center", gap: "4px", color: tokens.colorNeutralForeground2 },
});

type Filtro = "tutti" | StatoIscrizione;
const FILTRI: Filtro[] = ["tutti", "In scadenza", "Scaduta", "Da iscrivere", "Iscritta"];
const ETICHETTE: Record<Filtro, string> = {
  tutti: "Tutti",
  "In scadenza": "In scadenza",
  Scaduta: "Scaduti",
  "Da iscrivere": "Da iscrivere",
  Iscritta: "Iscritti",
};

/** Albi (iscrizioni agli elenchi fornitori): attivi o archiviati, ordinati per scadenza dell'iscrizione. */
export function ElencoAlbi({ archiviati }: { archiviati: boolean }) {
  const s = useStyles();
  const data = useData();
  const { byId } = useServizi();
  const [testo, setTesto] = useState("");
  const [filtro, setFiltro] = useState<Filtro>(() => {
    const f = new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("filtro");
    return f === "in-scadenza" ? "In scadenza" : "tutti";
  });
  const { data: schede, error, reload } = useAsync(() => loadGare(data, archiviati), [data, archiviati]);

  const visibili = useMemo(() => {
    if (!schede) return undefined;
    const t = testo.trim().toLowerCase();
    return ordinaAlbi(
      schede.filter(
        (g) =>
          eAlbo(g) &&
          (archiviati || filtro === "tutti" || statoIscrizione(g) === filtro || (filtro === "In scadenza" && statoIscrizione(g) === "Scaduta")) &&
          (!t || testoRicerca(g, byId.get(g.fields.Servizio ?? 0)?.fields.Sigla).includes(t)),
      ),
    );
  }, [schede, archiviati, filtro, testo, byId]);

  const apri = (g: Gara) => navigate(`/g/${g.id}`);

  return (
    <Shell
      title="Albi"
      actions={
        <>
          <Button appearance="subtle" icon={<ArrowClockwise24Regular />} aria-label="Aggiorna" onClick={reload} />
          {data.roles.gestore && (
            <Button appearance="primary" icon={<Add24Regular />} onClick={() => navigate("/nuovo-albo")}>
              Nuovo albo
            </Button>
          )}
        </>
      }
    >
      <div className={s.toolbar}>
        <ChipGroup
          ariaLabel="Vista"
          options={["attivi", "archiviati"] as const}
          labels={{ attivi: "In corso", archiviati: "Archiviati" }}
          value={archiviati ? "archiviati" : "attivi"}
          onChange={(v) => replace(v === "archiviati" ? "/albi/archiviati" : "/albi")}
        />
        {!archiviati && <ChipGroup ariaLabel="Stato dell'iscrizione" options={FILTRI} labels={ETICHETTE} value={filtro} onChange={setFiltro} />}
        <Input
          className={s.search}
          contentBefore={<Search20Regular />}
          placeholder="Cerca per codice, albo, ente…"
          value={testo}
          onChange={(_, d) => setTesto(d.value)}
        />
      </div>

      {error ? (
        <ErrorState error={error} />
      ) : !visibili ? (
        <Spinner style={{ marginTop: 48 }} />
      ) : (
        <>
          <Caption1 className={s.count}>
            {visibili.length} {visibili.length === 1 ? "albo" : "albi"}
            {filtro === "In scadenza" && !archiviati ? " in scadenza nei prossimi 30 giorni o già scaduti" : ""}
          </Caption1>
          {visibili.length === 0 ? (
            <Body1 className={s.empty}>Nessun albo corrisponde ai filtri.</Body1>
          ) : (
            <div className={s.tableWrap}>
              <Table aria-label="Albi">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell style={{ width: 230 }}>Codice</TableHeaderCell>
                    <TableHeaderCell>Albo ed ente</TableHeaderCell>
                    <TableHeaderCell style={{ width: 120 }}>Iscrizione</TableHeaderCell>
                    <TableHeaderCell style={{ width: 120 }}>Scadenza</TableHeaderCell>
                    <TableHeaderCell style={{ width: 170 }}>Stato</TableHeaderCell>
                    <TableHeaderCell style={{ width: 110 }}>Cartella</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibili.map((g) => (
                    <TableRow
                      key={g.id}
                      className={s.row}
                      tabIndex={0}
                      onClick={() => apri(g)}
                      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && apri(g)}
                    >
                      <TableCell>
                        <span className={s.codice}>
                          <Codice value={g.fields.Codice} />
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className={s.oggetto}>
                          <span className={s.titolo}>{oggettoGara(g)}</span>
                          <span className={s.sub}>{g.fields.Stazione}</span>
                        </div>
                      </TableCell>
                      <TableCell>{g.fields.DataIscrizione ? formatDate(g.fields.DataIscrizione) : "—"}</TableCell>
                      <TableCell>{g.fields.ScadenzaIscrizione ? formatDate(g.fields.ScadenzaIscrizione) : "—"}</TableCell>
                      <TableCell>
                        <IscrizioneBadge g={g} />
                      </TableCell>
                      <TableCell>
                        {g.fields.CartellaId ? (
                          <span className={s.cartella}>
                            <Folder20Regular /> Creata
                          </span>
                        ) : (
                          <span className={s.sub}>—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </Shell>
  );
}
